import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { recruitmentTheme, themeClasses } from '../styles/theme';
import {
  MessageSquare,
  Send,
  Users,
  Bot,
  Phone,
  CheckCircle,
  Clock,
  AlertCircle,
  Download,
  Upload
} from 'lucide-react';

interface WhatsAppMessage {
  _id: string;
  messageId: string;
  from: string;
  to: string;
  message: string;
  timestamp: string;
  type: string;
  processed: boolean;
  response?: string;
  relatedEntity?: {
    type: string;
    id: string;
  };
}

const WhatsAppPage: React.FC = () => {
  const [messages, setMessages] = useState<WhatsAppMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [newMessage, setNewMessage] = useState({
    to: '',
    message: '',
    type: 'text'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState({
    totalMessages: 0,
    processedMessages: 0,
    pendingMessages: 0,
    automatedActions: 0
  });

  useEffect(() => {
    fetchMessages();
    fetchStats();
  }, [currentPage]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/whatsapp/messages?page=${currentPage}&limit=20`);
      setMessages(response.data.data.messages);
      setTotalPages(response.data.data.pagination.pages);
    } catch (error) {
      console.error('Failed to fetch WhatsApp messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      // Calculate stats from messages
      const processed = messages.filter(m => m.processed).length;
      const pending = messages.filter(m => !m.processed).length;
      const automated = messages.filter(m => m.response).length;
      
      setStats({
        totalMessages: messages.length,
        processedMessages: processed,
        pendingMessages: pending,
        automatedActions: automated
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.to || !newMessage.message) {
      alert('Please provide phone number and message');
      return;
    }

    try {
      setSendingMessage(true);
      await axios.post(`${API_BASE_URL}/whatsapp/send`, newMessage);
      
      setNewMessage({ to: '', message: '', type: 'text' });
      alert('Message sent successfully!');
      await fetchMessages();
    } catch (error) {
      console.error('Failed to send message:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setSendingMessage(false);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPhoneNumber = (phone: string) => {
    if (phone.startsWith('254')) {
      return `+${phone}`;
    }
    return phone;
  };

  const getMessageTypeIcon = (type: string) => {
    switch (type) {
      case 'document':
        return <Download className="w-4 h-4 text-blue-500" />;
      case 'image':
        return <Upload className="w-4 h-4 text-green-500" />;
      case 'voice':
        return <Phone className="w-4 h-4 text-purple-500" />;
      default:
        return <MessageSquare className="w-4 h-4 text-gray-500" />;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`${themeClasses.pageTitle} ${recruitmentTheme.animations.fadeIn}`}>WhatsApp CRM Integration</h1>
          <p className="text-gray-600 mt-1">
            Automated WhatsApp communication and management system
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <div className="flex items-center space-x-1 text-green-600">
            <CheckCircle className="w-4 h-4" />
            <span className="text-sm">AI Automation Active</span>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className={`${recruitmentTheme.components.card} ${recruitmentTheme.animations.scaleIn}`}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-[#8b6914]">Total Messages</CardTitle>
            <MessageSquare className="h-4 w-4 text-[#8b6914]" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-[#8b6914]">{stats.totalMessages}</div>
            <p className="text-xs text-gray-600">
              All WhatsApp interactions
            </p>
          </CardContent>
        </div>

        <div className={`${recruitmentTheme.components.card} ${recruitmentTheme.animations.scaleIn}`}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-[#8b6914]">Processed</CardTitle>
            <Bot className="h-4 w-4 text-[#8b6914]" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-[#8b6914]">{stats.processedMessages}</div>
            <p className="text-xs text-gray-600">
              AI automated responses
            </p>
          </CardContent>
        </div>

        <div className={`${recruitmentTheme.components.card} ${recruitmentTheme.animations.scaleIn}`}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-[#8b6914]">Pending</CardTitle>
            <Clock className="h-4 w-4 text-[#8b6914]" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-[#8b6914]">{stats.pendingMessages}</div>
            <p className="text-xs text-gray-600">
              Awaiting processing
            </p>
          </CardContent>
        </div>

        <div className={`${recruitmentTheme.components.card} ${recruitmentTheme.animations.scaleIn}`}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-[#8b6914]">Actions Taken</CardTitle>
            <CheckCircle className="h-4 w-4 text-[#8b6914]" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-[#8b6914]">{stats.automatedActions}</div>
            <p className="text-xs text-gray-600">
              Automated workflows
            </p>
          </CardContent>
        </div>
      </div>

      {/* Send Message Card */}
      <div className={recruitmentTheme.components.card}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-[#8b6914]">
            <Send className="w-5 h-5 text-[#8b6914]" />
            Send WhatsApp Message
          </CardTitle>
          <CardDescription className="text-gray-600">Send messages directly to candidates, brokers, or team members</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="phone" className={`${recruitmentTheme.components.form.label} mb-1`}>
                Phone Number
              </label>
              <input
                id="phone"
                type="tel"
                placeholder="+254712345678"
                value={newMessage.to}
                onChange={(e) => setNewMessage({ ...newMessage, to: e.target.value })}
                className={recruitmentTheme.components.input}
              />
            </div>
            <div>
              <label htmlFor="type" className={`${recruitmentTheme.components.form.label} mb-1`}>
                Message Type
              </label>
              <select
                id="type"
                value={newMessage.type}
                onChange={(e) => setNewMessage({ ...newMessage, type: e.target.value })}
                className={recruitmentTheme.components.select}
              >
                <option value="text">Text Message</option>
                <option value="document">Document</option>
              </select>
            </div>
          </div>
          <div>
            <label htmlFor="message" className={`${recruitmentTheme.components.form.label} mb-1`}>
              Message
            </label>
            <textarea
              id="message"
              rows={4}
              placeholder="Type your message here..."
              value={newMessage.message}
              onChange={(e) => setNewMessage({ ...newMessage, message: e.target.value })}
              className={recruitmentTheme.components.textarea}
            />
          </div>
          <button 
            onClick={sendMessage} 
            disabled={sendingMessage}
            className={`${recruitmentTheme.components.button.primary} w-full md:w-auto`}
          >
            {sendingMessage ? 'Sending...' : 'Send Message'}
          </button>
        </CardContent>
      </div>

      {/* AI Commands Reference */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bot className="w-5 h-5 text-primary" />
            AI Command Reference
          </CardTitle>
          <CardDescription>Commands that users can send via WhatsApp for automated processing</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Candidate Management</h4>
              <div className="space-y-2 text-sm">
                <div className="bg-gray-50 p-2 rounded">
                  <code>"Add candidate John Doe for Driver"</code>
                  <p className="text-gray-600 mt-1">Creates new candidate profile</p>
                </div>
                <div className="bg-gray-50 p-2 rounded">
                  <code>"Status for John Doe"</code>
                  <p className="text-gray-600 mt-1">Check application status</p>
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Task Management</h4>
              <div className="space-y-2 text-sm">
                <div className="bg-gray-50 p-2 rounded">
                  <code>"Task ABC123 completed"</code>
                  <p className="text-gray-600 mt-1">Update task status</p>
                </div>
                <div className="bg-gray-50 p-2 rounded">
                  <code>"Task XYZ456 in progress"</code>
                  <p className="text-gray-600 mt-1">Update task progress</p>
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Scheduling</h4>
              <div className="space-y-2 text-sm">
                <div className="bg-gray-50 p-2 rounded">
                  <code>"Schedule interview with Jane at 2:00 PM"</code>
                  <p className="text-gray-600 mt-1">Book interview appointment</p>
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">General</h4>
              <div className="space-y-2 text-sm">
                <div className="bg-gray-50 p-2 rounded">
                  <code>"Help"</code>
                  <p className="text-gray-600 mt-1">Show available commands</p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Messages History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-primary" />
            WhatsApp Messages History
          </CardTitle>
          <CardDescription>Recent WhatsApp interactions and automated responses</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                {messages.map((message) => (
                  <div key={message._id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3">
                        {getMessageTypeIcon(message.type)}
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <span className="font-medium text-gray-900">
                              {formatPhoneNumber(message.from)}
                            </span>
                            <span className="text-sm text-gray-500">
                              {formatTimestamp(message.timestamp)}
                            </span>
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              message.processed 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {message.processed ? 'Processed' : 'Pending'}
                            </span>
                          </div>
                          <div className="bg-gray-50 p-3 rounded mb-2">
                            <p className="text-sm text-gray-800">{message.message}</p>
                          </div>
                          {message.response && (
                            <div className="bg-blue-50 p-3 rounded">
                              <div className="flex items-center space-x-1 mb-1">
                                <Bot className="w-4 h-4 text-blue-500" />
                                <span className="text-xs font-medium text-blue-700">AI Response</span>
                              </div>
                              <p className="text-sm text-blue-800">{message.response}</p>
                            </div>
                          )}
                          {message.relatedEntity && (
                            <div className="mt-2 text-xs text-gray-500">
                              Related: {message.relatedEntity.type} ({message.relatedEntity.id})
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {messages.length === 0 && (
                <div className="text-center py-12">
                  <MessageSquare className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No messages yet</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    WhatsApp messages will appear here once the integration is active.
                  </p>
                </div>
              )}

              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6">
                  <div className="text-sm text-gray-700">
                    Page {currentPage} of {totalPages}
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default WhatsAppPage;